import React, { useState } from "react";
import VisibilitySensor from "react-visibility-sensor";
import {
  generatePath,
  Link,
  useQueryParamObjectWith,
  useQueryParamString,
  useRouteMatch
} from "polyrouter";
import { useTranslation } from "utilities/i18n";
import { useSelector } from "react-redux";
import { State } from "store";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import {
  getColumns,
  TableBody,
  TableBulkItemRow,
  TableColumn,
  TableHeaderItem,
  TableSearchEdit
} from "collections/Table";
import { SortState } from "store/filters";
import { useFormattedLicenses } from "../hooks";
import { LicenseType } from "generated/graphql";

const LicensingTableRow: React.FC<{
  data: Record<string, string>;
  columns: TableColumn[];
  updateCursor: () => void;
}> = ({ data, columns, updateCursor }) => {
  const [previouslyVisible, setPreviouslyVisible] = useState(false);

  return (
    <VisibilitySensor
      active={!previouslyVisible}
      offset={{ bottom: -300 }}
      onChange={isVisible => {
        if (isVisible) {
          setPreviouslyVisible(isVisible);
          updateCursor();
        }
      }}
    >
      <TableBulkItemRow
        key={data.licenseKey}
        showChecks={false}
        onCheck={() => {}}
        checked={false}
      >
        {columns.map(({ key }) => (
          <td key={key}>
            {key === TABLES.LICENSES.COLUMNS.LICENSE_KEY ? (
              <Link
                nest
                to={generatePath("/details/:licenseKey", {
                  licenseKey: data.licenseKey
                })}
              >
                {data.licenseKey}
              </Link>
            ) : (
              data[key]
            )}
          </td>
        ))}
      </TableBulkItemRow>
    </VisibilitySensor>
  );
};

export const LicensingTable: React.FC = () => {
  const { t } = useTranslation();
  const showType = !!useRouteMatch("/manage/licensing/license-keys");
  const [currentType, setType, clearType] = useQueryParamString("type");
  const [currentSort, setSort] = useQueryParamObjectWith<"sort", SortState>(
    "sort",
    { field: "licenseKey", direction: "ASC" }
  );
  const { licenses, loading, updateCursor } = useFormattedLicenses();

  const { NAME: tableName, COLUMNS } = TABLES.LICENSES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumnKeys,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumnKeys
  } = useSelector((state: State) => {
    return state.userMeta?.tables[tableName] ?? {};
  });

  const columnTranslations = {
    [COLUMNS.LICENSE_KEY]: t("KEY"),
    [COLUMNS.TYPE]: t("TYPE"),
    [COLUMNS.ACTIVATION_DATE]: t("DATE_ADDED"),
    [COLUMNS.END_DATE]: t("EXPIRATION"),
    [COLUMNS.QUANTITY]: t("QUANTITY"),
    [COLUMNS.USER_ID]: t("ACTIVATED_BY")
  };

  const shownColumns = getColumns(
    shownColumnKeys.filter(key => showType || key !== COLUMNS.TYPE),
    columnTranslations
  );
  const hiddenColumns = getColumns(
    hiddenColumnKeys.filter(key => showType || key !== COLUMNS.TYPE),
    columnTranslations
  );

  const getSortProps = (key: string) => {
    if (key !== "type") {
      return {
        currentSort,
        onSort: (sort: SortState) => setSort(sort)
      };
    }
  };

  const getFilterProps = (key: string) => {
    if (key === "type") {
      return {
        filterOptions: [
          {
            text: t(LicenseType.PolyPlus),
            value: LicenseType.PolyPlus
          },
          {
            text: t(LicenseType.Clariti),
            value: LicenseType.Clariti
          }
        ],
        filterValue: currentType ?? "",
        onFilter: (_: string, value: string) => {
          value.length ? setType(value) : clearType();
        }
      };
    }
  };

  return (
    <>
      <div
        className="grouping mb-15 table-actions"
        data-testid="licensing-table"
      >
        <TableSearchEdit
          search={{ onSearch: () => {} }}
          edit={{ tableName, shownColumns, hiddenColumns }}
        />
      </div>
      <div className="table-container mb-4">
        <table className="table custom-table">
          <thead>
            <tr>
              {shownColumns.map(({ key, title }) => (
                <TableHeaderItem
                  key={key}
                  fieldName={key}
                  title={title}
                  sort={getSortProps(key)}
                  filter={getFilterProps(key)}
                />
              ))}
            </tr>
          </thead>

          <TableBody
            loading={loading}
            cols={shownColumns.length - 1}
            accommodateScrollbar
          >
            {licenses.map((rowData, index) => (
              <LicensingTableRow
                key={rowData.licenseKey}
                data={rowData}
                columns={shownColumns}
                updateCursor={() => updateCursor(index)}
              />
            ))}
          </TableBody>
        </table>
      </div>
    </>
  );
};
